/**
 * Das Analysefeld rechts neben dem Dokument.
 *
 * Links liegt der Beleg, rechts die Auswertung — nebeneinander, weil beim
 * Lesen einer Analyse staendig die Frage kommt "wo steht das". Wer dafuer die
 * Ansicht wechseln muss, prueft nicht nach.
 *
 * Die Analyse ist Markdown. Eine Quelle, zwei Ausgaben: gelesen wird sie hier,
 * mitgenommen als PDF. Die PDF druckt der Server mit demselben Renderer
 * (web/src/druck.tsx, server/src/druck/pdf.ts). Beim BWA Monatsbericht steht
 * statt des Texts die Ansicht mit Kennzahlen und Verlaeufen (bwabericht/);
 * PDF und Bearbeiten bleiben beim Text.
 */
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import type { Editor } from '@tiptap/react'
import { api } from '../lib/api'
import { Markdown } from '../lib/markdown'
import { BwaBericht } from './bwabericht/BwaBericht'
import type { Monatsbericht } from './bwabericht/typen'
import { Mehrmenue } from './Mehrmenue'
import { Fenster } from './Fenster'
/* Der Editor wird erst geladen, wenn jemand bearbeitet: Er ist groesser als
   der Rest der Ansicht, und die meisten lesen nur. */
const AnalyseEditor = lazy(() => import('./AnalyseEditor').then((m) => ({ default: m.AnalyseEditor })))

interface Analyse {
  inhalt: string
  skill_fassung: string | null
  erstellt_am: string
  geaendert_am: string
  erstellt_von_name: string | null
}

/** Stand einer Analyse, die im Hintergrund auf dem Server laeuft. */
interface AnalyseStand {
  status: 'laeuft' | 'fertig' | 'fehler'
  phase: string | null
  schritt: string
  fehler?: string
  begonnen: string
}

/** Der juengste eigene Bericht zu einem spaeteren Stand; dann wird dieses BWA Paket nicht mehr analysiert. */
interface Spaeter {
  dokumentId: string | null
  periode: string
  stand: string
}

/* Die Abschnitte des Ablaufs je Werkzeug, wie der Server sie meldet, in Worten der Kanzlei. */
const ABLAUF = {
  bwa: {
    phasen: [
      { phase: 'einlesen', text: 'BWA Pakete einlesen' },
      { phase: 'regeln', text: 'Zahlen mit 29 Regeln über die Monate prüfen' },
      { phase: 'ki', text: 'KI analysiert und bohrt nach' },
      { phase: 'pruefen', text: 'Bericht auf Belege und Vollständigkeit prüfen' },
      { phase: 'ablegen', text: 'Bericht ablegen' },
    ],
    dauer: 'meist zwei bis vier Minuten',
  },
  jahresabschluss: {
    phasen: [
      { phase: 'bereitstellen', text: 'Abschluss bereitstellen' },
      { phase: 'lesen', text: 'Bericht vollständig lesen, Zahlen abschreiben und beweisen' },
      { phase: 'kennzahlen', text: 'Kennzahlen rechnen, mit der Branche vergleichen' },
      { phase: 'beurteilen', text: 'Lage, Ergebnisqualität und Krisenindizien beurteilen' },
      { phase: 'steuern', text: 'Steuerliche Prüfanlässe am Gesetzestext' },
      { phase: 'bericht', text: 'Beratungsanlässe bilden, Bericht schreiben' },
      { phase: 'pruefen', text: 'Selbstprüfung, jede Norm gegen das Gesetz' },
      { phase: 'ablegen', text: 'Analyse ablegen' },
    ],
    dauer: 'meist zehn bis zwanzig Minuten',
  },
} as const
type Werkzeug = keyof typeof ABLAUF

/** Was gerade im aktiven Abschnitt passiert, ohne die Wiederholung des Abschnitts. */
function feinheit(stand: AnalyseStand): string | null {
  const klammer = /\(([^)]+)\)/.exec(stand.schritt)
  if (klammer) return klammer[1]!
  if (stand.schritt === 'Bericht wird nachgebessert') return 'die KI bessert Mängel nach'
  return null
}

/** Fortschritt als Liste: erledigt mit Haken, aktiv mit Rädchen, offen blass. */
function Fortschritt({ stand, werkzeug }: { stand: AnalyseStand; werkzeug: Werkzeug }) {
  const { phasen, dauer: ueblich } = ABLAUF[werkzeug]
  const aktiv = Math.max(0, phasen.findIndex((p) => p.phase === stand.phase))
  const fein = feinheit(stand)
  return (
    <div className="fortschritt" role="status" aria-live="polite">
      <div className="fortschritt-kopf">
        <span className="raedchen" aria-hidden="true" />
        <p className="analysefeld-leer-titel">Analyse läuft</p>
      </div>
      <ol className="fortschritt-schritte">
        {phasen.map((p, i) => {
          const zustand = i < aktiv ? 'erledigt' : i === aktiv ? 'aktiv' : 'offen'
          return (
            <li key={p.phase} className={`fortschritt-schritt ${zustand}`}>
              <span className="fortschritt-zeichen" aria-hidden="true">
                {zustand === 'erledigt' ? '✓' : zustand === 'aktiv' ? <span className="raedchen klein" /> : ''}
              </span>
              <span>
                {p.text}
                {zustand === 'aktiv' && fein && <span className="fortschritt-fein">{fein}</span>}
              </span>
            </li>
          )
        })}
      </ol>
      <p className="fortschritt-fuss nebensache">
        Läuft seit {dauer(stand.begonnen)}, {ueblich}. Die Seite darf geschlossen werden, die Analyse
        läuft auf dem Server weiter.
      </p>
    </div>
  )
}

/** Wie lange etwas schon laeuft, in Minuten und Sekunden. */
function dauer(seit: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(seit).getTime()) / 1000))
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`
}

/** Nur das Datum: wann die Analyse entstanden oder zuletzt geaendert wurde. */
function stand(datum: string): string {
  const d = new Date(datum)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function alsText(analyse: Analyse) {
  return (
    <article className="analysefeld-text">
      <Markdown text={analyse.inhalt} />
      <p className="analysefeld-schluss nebensache">Kanzleiinternes Arbeitspapier</p>
    </article>
  )
}

export function Analysefeld({
  dokumentId,
  bwa = false,
  daten,
  beiKompakt,
}: {
  dokumentId: string
  bwa?: boolean
  /** Der Datenbereich darueber, wenn er im kompakten Kopf ausgeblendet ist: ein Eintrag im Menue blendet ihn ein. */
  daten?: { sichtbar: boolean; umschalten: () => void }
  /** Meldet, ob der Kopf kompakt ist (BWA mit Monatsbericht). */
  beiKompakt?: (kompakt: boolean) => void
}) {
  const kunde = useQueryClient()
  const [bearbeiten, setBearbeiten] = useState(false)
  const [geaendert, setGeaendert] = useState(false)
  const editorRef = useRef<Editor | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  const abfrage = useQuery({
    queryKey: ['analyse', dokumentId],
    queryFn: () => api.holen<{ analyse: Analyse | null }>(`/api/dokumente/${dokumentId}/analyse`),
  })
  const analyse = abfrage.data?.analyse ?? null

  const speichern = useMutation({
    mutationFn: (inhalt: string) =>
      api.aendern<{ analyse: Analyse }>(`/api/dokumente/${dokumentId}/analyse`, { inhalt }),
    onSuccess: () => {
      setBearbeiten(false)
      void kunde.invalidateQueries({ queryKey: ['analyse', dokumentId] })
      void kunde.invalidateQueries({ queryKey: ['dokument', dokumentId] })
      void kunde.invalidateQueries({ queryKey: ['dokumente'] })
    },
  })

  /* Eine Meldung wie "kopiert" soll von selbst wieder verschwinden. Acht
     Sekunden, nicht vier: manche Meldung nennt einen naechsten Schritt, und
     den muss man zu Ende lesen koennen. */
  useEffect(() => {
    if (!meldung) return
    const uhr = setTimeout(() => setMeldung(null), 8000)
    return () => clearTimeout(uhr)
  }, [meldung])

  /* Die Analyse laeuft im Hintergrund auf dem Server (BWA: Einlesen, Regeln,
     Monatsbericht; Jahresabschluss: der Skill ueber alle Stufen). Solange
     etwas laeuft, fragt das Feld alle zwei Sekunden nach. */
  const werkzeug: Werkzeug = bwa ? 'bwa' : 'jahresabschluss'
  const laufAbfrage = useQuery({
    queryKey: ['analyse-lauf', werkzeug, dokumentId],
    queryFn: () => api.holen<{ ki: boolean; stand: AnalyseStand | null; spaeter?: Spaeter | null }>(`/api/dokumente/${dokumentId}/${werkzeug}/analyse`),
    refetchInterval: (q) => (q.state.data?.stand?.status === 'laeuft' ? 2000 : false),
    // Auch im Hintergrund nachfragen: wer waehrend der Analyse etwas anderes ansieht, soll beim Zurueckkommen das Ergebnis sehen.
    refetchIntervalInBackground: true,
  })
  const laufStand = laufAbfrage.data?.stand ?? null
  const spaeter = laufAbfrage.data?.spaeter ?? null
  const laeuft = laufStand?.status === 'laeuft'

  /* Ist die Analyse fertig, alles neu holen, was sich dabei geaendert hat. */
  const zuletzt = useRef<string | null>(null)
  useEffect(() => {
    const status = laufStand?.status ?? null
    if (zuletzt.current === 'laeuft' && status === 'fertig') {
      void kunde.invalidateQueries({ queryKey: ['analyse', dokumentId] })
      void kunde.invalidateQueries({ queryKey: ['bwa-bericht', dokumentId] })
      void kunde.invalidateQueries({ queryKey: ['bwa-einlesung'] })
      void kunde.invalidateQueries({ queryKey: ['dokument', dokumentId] })
      void kunde.invalidateQueries({ queryKey: ['dokumente'] })
    }
    zuletzt.current = status
  }, [laufStand?.status, dokumentId, kunde])

  const starten = useMutation({
    mutationFn: () => api.senden<{ stand: AnalyseStand }>(`/api/dokumente/${dokumentId}/${werkzeug}/analyse`),
    onSuccess: () => void kunde.invalidateQueries({ queryKey: ['analyse-lauf', werkzeug, dokumentId] }),
    onError: (fehler) => setMeldung(fehler instanceof Error ? fehler.message : 'Die Analyse konnte nicht starten.'),
  })

  /* Ohne KI Zugang sagt der Knopf das offen, statt sich stumm oder tot zu
     stellen — ein Knopf ohne Wirkung und ohne Erklaerung ist schlimmer als keiner. */
  function analysieren() {
    if (laufAbfrage.data && !laufAbfrage.data.ki) {
      setMeldung('Die KI ist auf diesem Server noch nicht eingerichtet. Sobald der Zugang hinterlegt ist, startet der Knopf die Analyse.')
      return
    }
    starten.mutate()
  }

  /* Die Laufzeit zaehlt sichtbar mit, damit drei Minuten Warten nicht wie ein Haenger aussehen. */
  const [, takt] = useState(0)
  useEffect(() => {
    if (!laeuft) return
    const uhr = setInterval(() => takt((n) => n + 1), 1000)
    return () => clearInterval(uhr)
  }, [laeuft])

  /* Die PDF erzeugt der Server. Das dauert einen Moment; solange zeigt der
     Knopf, dass er arbeitet, und laesst sich nicht ein zweites Mal druecken. */
  const pdf = useMutation({
    mutationFn: () => api.herunterladen(`/api/dokumente/${dokumentId}/analyse/pdf`),
    onError: (fehler) =>
      setMeldung(fehler instanceof Error ? fehler.message : 'Die PDF konnte nicht erstellt werden.'),
  })

  /* Loeschen nimmt die eigene Analyse mit allem, was sie gespeichert hat,
     wieder heraus; danach laesst sich neu analysieren. Bestaetigt wird in
     einem Fenster, ein Fehlklick soll nichts loeschen. */
  const [loeschenOffen, setLoeschenOffen] = useState(false)
  const loeschen = useMutation({
    mutationFn: () => api.loeschen<{ geloescht: Record<string, number> }>(`/api/dokumente/${dokumentId}/analyse`),
    onSuccess: () => {
      setLoeschenOffen(false)
      for (const schluessel of [['analyse', dokumentId], ['bwa-bericht', dokumentId], ['analyse-lauf'], ['dokument', dokumentId], ['dokumente']]) {
        void kunde.invalidateQueries({ queryKey: schluessel })
      }
    },
  })

  const stempel = analyse ? `Stand ${stand(analyse.geaendert_am)}` : null

  /* Gibt es zur BWA den Monatsbericht, traegt dessen Kopf Titel, Daten und
     Knoepfe; die Leiste "Analyse" darueber entfaellt. Dieselbe Abfrage wie
     in BwaBericht, also kein zweiter Aufruf. */
  const bericht = useQuery({
    queryKey: ['bwa-bericht', dokumentId, analyse?.geaendert_am],
    queryFn: () => api.holen<{ bericht: Monatsbericht | null }>(`/api/dokumente/${dokumentId}/bwa/bericht`),
    enabled: bwa && !!analyse,
  })
  const kompakt = bwa && !!analyse && !bearbeiten && !!bericht.data?.bericht
  useEffect(() => {
    beiKompakt?.(kompakt)
  }, [kompakt, beiKompakt])

  function bearbeitenBeginnen() {
    setGeaendert(false)
    speichern.reset()
    setBearbeiten(true)
  }

  /* Fertig speichert nur, wenn wirklich etwas geaendert wurde: der Editor
     schreibt Tabellen buendig aufgefuellt zurueck, und ein blosses Oeffnen und
     Schliessen soll die gespeicherte Analyse nicht umformatieren. */
  function fertig() {
    const editor = editorRef.current
    if (!geaendert || !editor) {
      setBearbeiten(false)
      return
    }
    speichern.mutate(editor.getMarkdown())
  }

  return (
    <aside className="analysefeld">
      {!kompakt && <div className="analysefeld-kopf">
        <div className="analysefeld-titel">
          <h2>Analyse</h2>
          {stempel && !bearbeiten && <span className="nebensache">{stempel}</span>}
        </div>

        {analyse && !bearbeiten && (
          <div className="analysefeld-werkzeuge">
            <button type="button" className="knopf" disabled={pdf.isPending} onClick={() => pdf.mutate()}>
              {pdf.isPending ? 'PDF wird erstellt …' : 'PDF'}
            </button>
            <button type="button" className="knopf" onClick={bearbeitenBeginnen}>
              Bearbeiten
            </button>
            <button
              type="button"
              className="knopf"
              onClick={() => {
                loeschen.reset()
                setLoeschenOffen(true)
              }}
            >
              Löschen
            </button>
          </div>
        )}

        {analyse && bearbeiten && (
          <div className="analysefeld-werkzeuge">
            <button type="button" className="knopf still" onClick={() => setBearbeiten(false)}>
              Abbrechen
            </button>
            <button type="button" className="knopf gefuellt" disabled={speichern.isPending} onClick={fertig}>
              {speichern.isPending ? 'Wird gespeichert …' : 'Fertig'}
            </button>
          </div>
        )}
      </div>}

      {meldung && <p className="analysefeld-meldung">{meldung}</p>}

      <div className="analysefeld-koerper">
        {abfrage.isPending && <div className="laedt">Wird geladen …</div>}

        {abfrage.isError && (
          <div className="fehler">
            {abfrage.error instanceof Error ? abfrage.error.message : 'Die Analyse ist nicht abrufbar.'}
          </div>
        )}

        {bearbeiten && analyse && (
          <div className="analysefeld-bearbeiten">
            {speichern.isError && (
              <div className="fehler">
                {speichern.error instanceof Error ? speichern.error.message : 'Speichern hat nicht geklappt.'}
              </div>
            )}
            <Suspense fallback={<div className="laedt">Editor wird geladen …</div>}>
              <AnalyseEditor
                inhalt={analyse.inhalt}
                onBereit={(e) => {
                  editorRef.current = e
                }}
                onGeaendert={() => setGeaendert(true)}
              />
            </Suspense>
          </div>
        )}

        {/* Die leere Seite ist die, die der Berufstraeger als Erstes sieht.
            Sie sagt in einem Satz, was hier entsteht, und bietet den Weg
            dorthin an — nicht mehr. */}
        {!bearbeiten && !abfrage.isPending && !analyse && laeuft && laufStand && (
          <div className="analysefeld-leer">
            <Fortschritt stand={laufStand} werkzeug={werkzeug} />
          </div>
        )}

        {/* Ein aelteres BWA Paket, nachdem es schon einen spaeteren Bericht gibt:
            kein Knopf, der nie klappen kann, sondern der Weg zum aktuellen Bericht. */}
        {!bearbeiten && !abfrage.isPending && !analyse && !laeuft && spaeter && (
          <div className="analysefeld-leer">
            <p className="analysefeld-leer-titel">Wird nicht nachträglich analysiert</p>
            <p className="analysefeld-leer-text">
              Monatsberichte entstehen in zeitlicher Folge, jeder baut auf dem vorigen auf. Es gibt schon einen Bericht zu
              einem späteren Stand ({spaeter.periode}). Wer ältere Pakete analysieren will, löscht zuerst diesen Bericht.
            </p>
            {spaeter.dokumentId && (
              <Link className="knopf" to={`/bwa/dokumente/${spaeter.dokumentId}`}>
                Zum Bericht {spaeter.periode}
              </Link>
            )}
          </div>
        )}

        {!bearbeiten && !abfrage.isPending && !analyse && !laeuft && !spaeter && (
          <div className="analysefeld-leer">
            <p className="analysefeld-leer-titel">{laufStand?.status === 'fehler' ? 'Analyse nicht fertig geworden' : 'Noch nicht analysiert'}</p>
            {laufStand?.status === 'fehler' && laufStand.fehler ? (
              <p className="analysefeld-leer-text">{laufStand.fehler}</p>
            ) : bwa ? (
              <p className="analysefeld-leer-text">
                Liest die BWA Pakete des Mandanten ein, prüft sie mit 29 Regeln über die Monate und schreibt mit der KI
                den Monatsbericht: Beratungsanlässe, Punkte für die Buchhaltung, was seit dem Vormonat erledigt ist.
                Jede Zahl mit Beleg.
              </p>
            ) : (
              <p className="analysefeld-leer-text">
                Die Auswertung prüft die Zahlen, gliedert sie um, hält sie gegen die Branche und legt daraus
                Beratungsanlässe vor. Jede Aussage mit der Stelle im Abschluss daneben.
              </p>
            )}
            {/* Ein Weg, nicht zwei. Ein zweiter Knopf daneben wirft die Frage
                auf, wann man welchen nimmt — und hier gibt es darauf keine
                Antwort, die einem Berufstraeger etwas sagt. Korrigiert wird
                spaeter ueber "Bearbeiten", wenn ein Text dasteht, den man
                korrigieren kann. */}
            <button type="button" className="knopf gefuellt" disabled={starten.isPending} onClick={analysieren}>
              {laufStand?.status === 'fehler' ? 'Noch einmal versuchen' : 'Analyse erstellen'}
            </button>
          </div>
        )}

        {!bearbeiten &&
          analyse &&
          (bwa ? (
            <BwaBericht
              dokumentId={dokumentId}
              stand={analyse.geaendert_am}
              ersatz={alsText(analyse)}
              analysiert={stand(analyse.geaendert_am)}
              werkzeuge={
                <>
                  <button type="button" className="knopf" disabled={pdf.isPending} onClick={() => pdf.mutate()}>
                    {pdf.isPending ? 'PDF wird erstellt …' : 'PDF'}
                  </button>
                  <Mehrmenue
                    eintraege={[
                      ...(daten ? [{ text: daten.sichtbar ? 'Eingelesene Daten ausblenden' : 'Eingelesene Daten anzeigen', aktion: daten.umschalten }] : []),
                      { text: 'Text bearbeiten', aktion: bearbeitenBeginnen },
                      {
                        text: 'Analyse löschen',
                        gefahr: true,
                        aktion: () => {
                          loeschen.reset()
                          setLoeschenOffen(true)
                        },
                      },
                    ]}
                  />
                </>
              }
            />
          ) : (
            alsText(analyse)
          ))}
      </div>

      <Fenster titel="Analyse löschen" offen={loeschenOffen} aufSchliessen={() => setLoeschenOffen(false)}>
        <div className="formular">
          {loeschen.isError && <div className="fehler">{loeschen.error instanceof Error ? loeschen.error.message : 'Löschen hat nicht geklappt.'}</div>}
          <p>
            {bwa
              ? 'Der Monatsbericht wird mit allem gelöscht, was die KI dazu gespeichert hat. Die eingelesenen Zahlen bleiben. Danach lässt sich dieses Paket neu analysieren, ebenso ältere.'
              : 'Die Analyse wird gelöscht. Danach lässt sich der Abschluss neu analysieren.'}
          </p>
          <p className="nebensache">Rückmeldungen zu dieser Analyse bleiben erhalten.</p>
          <div className="formular-fuss">
            <button type="button" className="knopf still" onClick={() => setLoeschenOffen(false)}>
              Abbrechen
            </button>
            <button type="button" className="knopf gefuellt" disabled={loeschen.isPending} onClick={() => loeschen.mutate()}>
              {loeschen.isPending ? 'Wird gelöscht …' : 'Löschen'}
            </button>
          </div>
        </div>
      </Fenster>
    </aside>
  )
}
